/* @pareto-engineering/generator-front 1.0.12 */
import * as React from 'react';
import { useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { format, differenceInDays, formatDistance, formatRelative } from 'date-fns';
import styleNames from '@pareto-engineering/bem/exports';
import "./styles.scss";

// Local Definitions

const baseClassName = styleNames.base;
const componentClassName = 'timestamp';

/**
 * This is the component description.
 */
const Timestamp = ({
  id,
  className: userClassName,
  style,
  time,
  prefix,
  enabledFormats,
  distanceMaxDays,
  relativeMaxDays,
  dateFormat,
  locale,
  // ...otherProps
  as: Element
}) => {
  const [currentDisplay, setCurrentDisplay] = useState(enabledFormats[0]);
  const formatMap = useMemo(() => ({
    unix: unix => Number(unix),
    date: unix => format(new Date(unix), dateFormat),
    distance: unix => !distanceMaxDays || differenceInDays(Date.now(), new Date(unix)) < distanceMaxDays ? formatDistance(new Date(unix), new Date(), {
      addSuffix: true,
      locale
    }) : format(new Date(unix), dateFormat),
    relative: unix => !relativeMaxDays || differenceInDays(Date.now(), new Date(unix)) < relativeMaxDays ? formatRelative(new Date(unix), new Date(), {
      locale
    }) : format(new Date(unix), dateFormat)
  }), []);
  const onClick = useCallback(e => {
    e.persist();
    // eslint-disable-next-line no-shadow
    const currentIndex = enabledFormats.findIndex(e => e === currentDisplay);
    const nextIndex = (currentIndex + 1) % enabledFormats.length;
    setCurrentDisplay(enabledFormats[nextIndex]);
  }, [currentDisplay]);
  if (!Number(time)) return null;
  return /*#__PURE__*/React.createElement(Element, {
    id: id,
    className: [baseClassName, componentClassName, userClassName].filter(e => e).join(' '),
    style: style
    // {...otherProps}
    ,
    onClick: enabledFormats.length > 1 ? onClick : undefined
  }, prefix && prefix, prefix && ' ', formatMap[currentDisplay](Number(time)));
};
Timestamp.propTypes = {
  /**
   * The HTML id for this element
   */
  id: PropTypes.string,
  /**
   * The HTML class names for this element
   */
  className: PropTypes.string,
  /**
   * The React-written, css properties for this element.
   */
  style: PropTypes.objectOf(PropTypes.string),
  /**
   *  The children JSX
   */
  prefix: PropTypes.node,
  /**
    * Which html tag to use
    */
  as: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
    * Formats
    */
  enabledFormats: PropTypes.arrayOf(PropTypes.oneOf(['unix', 'date', 'distance', 'relative'])),
  /**
   * How many days to display distance before reverting to unix display
   */
  distanceMaxDays: PropTypes.number,
  /**
   * How many days to display distance before reverting to unix display
   */
  relativeMaxDays: PropTypes.number,
  /**
    * Unix Format Read https://date-fns.org/v2.16.1/docs/format for options
    */
  dateFormat: PropTypes.string,
  /**
    * The time to display
    */
  time: PropTypes.oneOfType([PropTypes.object, PropTypes.number]).isRequired,
  /**
    * A Date-DNS locale object. Will default to english.
    */
  locale: PropTypes.instanceOf(Object)
};
Timestamp.defaultProps = {
  // someProp:false
  as: 'p',
  dateFormat: 'yyyy-MM-dd\'T\'HH:mm:ss.SSSxxx',
  enabledFormats: ['unix', 'date']
};
export default Timestamp;